/**
 * Documents page — drives BOTH /books/invoices AND /books/bills.
 * Picks its kind from the URL path (invoices → invoice, bills → bill).
 * Handles: list, draft-create, edit, send (auto-journal), record payment, credit/debit note.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2, Plus, Send, X, Trash2, Receipt as ReceiptIcon, RotateCcw, FileDown } from 'lucide-react';
import { booksClient, getBooksTenant, BOOKS_API, errMsg } from './api';
import { toast } from '../hooks/use-toast';

const KIND_META = {
  invoice: { label: 'Invoices', singular: 'Invoice', settleLabel: 'Record Receipt', noteKind: 'credit_note', noteLabel: 'Credit Note', contactType: 'customer', payKind: 'receipt' },
  bill:    { label: 'Bills',    singular: 'Bill',    settleLabel: 'Record Payment', noteKind: 'debit_note',  noteLabel: 'Debit Note',  contactType: 'vendor',   payKind: 'payment' },
};

const STATUS_STYLE = {
  draft:   'bg-slate-200 text-slate-700',
  sent:    'bg-amber-100 text-amber-800',
  partial: 'bg-blue-100 text-blue-800',
  paid:    'bg-emerald-100 text-emerald-800',
  overdue: 'bg-red-100 text-red-800',
  issued:  'bg-slate-100 text-slate-700',
};

const fmt = (n, cur) => {
  const sym = { GBP: '£', INR: '₹', MYR: 'RM ', KZT: '₸', USD: '$', EUR: '€' }[cur] || '';
  return `${sym}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const emptyLine = () => ({ description: '', quantity: 1, unit_price: 0, account_id: '', tax_code: '', cost_center: '' });

const CCY_LIST = ['GBP', 'USD', 'EUR', 'INR', 'MYR', 'KZT', 'SGD', 'AED', 'CNY', 'JPY'];

const DocumentsPage = () => {
  const loc = useLocation();
  const kind = loc.pathname.includes('bills') ? 'bill' : 'invoice';
  const meta = KIND_META[kind];
  const cur = getBooksTenant()?.base_currency;

  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [costCentres, setCostCentres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [openPay, setOpenPay] = useState(null); // doc obj

  const load = () => {
    setLoading(true);
    Promise.all([
      booksClient.get('/documents', { params: { kind } }),
      booksClient.get('/contacts', { params: { type: meta.contactType } }),
      booksClient.get('/ledger/accounts'),
      booksClient.get('/masters/tax-codes'),
      booksClient.get('/cost-centres'),
    ]).then(([d, c, a, t, cc]) => {
      setItems(d.data.data || []);
      setContacts(c.data.data || []);
      setAccounts(a.data.data || []);
      setTaxes(t.data.data || []);
      setCostCentres(cc.data.data || []);
    }).finally(() => setLoading(false));
  };
  useEffect(load, [kind]); // eslint-disable-line

  const sendDoc = async (id) => {
    if (!window.confirm(`Send ${meta.singular.toLowerCase()}? This locks the entry and posts a journal.`)) return;
    try {
      await booksClient.post(`/documents/${id}/send`);
      toast({ title: `${meta.singular} sent — journal posted` });
      load();
    } catch (e) { toast({ title: errMsg(e), variant: 'destructive' }); }
  };

  const downloadPdf = async (doc_id, doc_no, style) => {
    try {
      const r = await booksClient.get(`/documents/${doc_id}/pdf`, { params: { style }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement('a');
      link.href = url; link.download = `${doc_no}-${style}.pdf`;
      link.click(); URL.revokeObjectURL(url);
    } catch (e) { toast({ title: 'PDF download failed', variant: 'destructive' }); }
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold tracking-[0.3em] text-[#0f5c3d] uppercase">{kind === 'invoice' ? 'Sales' : 'Purchases'}</div>
          <h1 className="text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>{meta.label}</h1>
        </div>
        <button onClick={() => setOpenNew(true)} data-testid={`new-${kind}-btn`}
          className="inline-flex items-center gap-1 px-4 py-2 bg-[#0f5c3d] text-white rounded-full text-sm font-semibold">
          <Plus className="w-4 h-4" /> New {meta.singular}
        </button>
      </div>

      {loading ? <div className="p-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> :
      items.length === 0 ? (
        <div className="p-12 text-center text-[#0b1a2e]/40 italic bg-white rounded-2xl border border-black/5">No {meta.label.toLowerCase()} yet.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">
              <tr>
                <th className="text-left px-4 py-2">Doc No</th>
                <th className="text-left px-4 py-2">Date</th>
                <th className="text-left px-4 py-2">{meta.contactType === 'customer' ? 'Customer' : 'Vendor'}</th>
                <th className="text-right px-4 py-2">Total</th>
                <th className="text-right px-4 py-2">Due</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2 w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.doc_id} className="border-t border-slate-100 hover:bg-[#f7fbf8]">
                  <td className="px-4 py-2 font-mono text-xs">{d.doc_no}</td>
                  <td className="px-4 py-2 text-xs text-[#0b1a2e]/70">{d.date}</td>
                  <td className="px-4 py-2">{d.contact_name || '—'}</td>
                  <td className="px-4 py-2 text-right font-mono">
                    {fmt(d.grand_total, d.currency || cur)}
                    {d.currency && d.currency !== cur && (
                      <div className="text-[10px] text-[#0b1a2e]/50">≈ {fmt(d.base_grand_total, cur)}</div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right font-mono">{fmt(d.amount_due, d.currency || cur)}</td>
                  <td className="px-4 py-2">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${STATUS_STYLE[d.status] || 'bg-slate-100'}`}>{d.status}</span>
                  </td>
                  <td className="px-4 py-2 text-right space-x-1">
                    {d.status === 'draft' && (
                      <button onClick={() => sendDoc(d.doc_id)} title="Send" data-testid={`doc-send-${d.doc_id}`}
                        className="text-[#0f5c3d] hover:text-[#0b1a2e]"><Send className="w-3.5 h-3.5" /></button>
                    )}
                    {(d.status === 'sent' || d.status === 'partial') && (
                      <button onClick={() => setOpenPay(d)} title={meta.settleLabel} data-testid={`doc-pay-${d.doc_id}`}
                        className="text-blue-700 hover:text-blue-900"><ReceiptIcon className="w-3.5 h-3.5" /></button>
                    )}
                    <button onClick={() => downloadPdf(d.doc_id, d.doc_no, 'corporate')} title="Corporate PDF" data-testid={`doc-pdf-corp-${d.doc_id}`}
                      className="text-[#0b1a2e]/60 hover:text-[#0b1a2e]"><FileDown className="w-3.5 h-3.5" /></button>
                    <button onClick={() => downloadPdf(d.doc_id, d.doc_no, 'modern')} title="Modern PDF" data-testid={`doc-pdf-mod-${d.doc_id}`}
                      className="text-[#0f5c3d]/70 hover:text-[#0f5c3d]"><FileDown className="w-3.5 h-3.5" fill="currentColor" fillOpacity="0.2" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openNew && (
        <DocumentEditor
          kind={kind} meta={meta} contacts={contacts} accounts={accounts} taxes={taxes} costCentres={costCentres} cur={cur}
          onClose={() => setOpenNew(false)}
          onSaved={() => { setOpenNew(false); load(); }}
        />
      )}

      {openPay && (
        <PaymentEditor
          doc={openPay} kind={meta.payKind} accounts={accounts} cur={cur}
          onClose={() => setOpenPay(null)}
          onSaved={() => { setOpenPay(null); load(); }}
        />
      )}
    </div>
  );
};

// ---------------- Document editor (draft create) ----------------
const DocumentEditor = ({ kind, meta, contacts, accounts, taxes, costCentres, cur, onClose, onSaved }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    kind, contact_id: '', date: today, reference: '', notes: '',
    currency: cur, exchange_rate_to_base: '',
    lines: [emptyLine()],
  });
  const [fxLoading, setFxLoading] = useState(false);

  const setLine = (i, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, ...patch } : l) }));
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, emptyLine()] }));
  const rmLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  // Auto-lookup FX rate whenever currency or date changes and it's not base
  useEffect(() => {
    if (!form.currency || form.currency === cur) { setForm((f) => ({ ...f, exchange_rate_to_base: '' })); return; }
    setFxLoading(true);
    booksClient.get('/fx/lookup', { params: { from_currency: form.currency, to_currency: cur, on_date: form.date } })
      .then((r) => setForm((f) => ({ ...f, exchange_rate_to_base: r.data.rate })))
      .finally(() => setFxLoading(false));
  }, [form.currency, form.date, cur]);

  const totals = useMemo(() => {
    let sub = 0, tax = 0;
    form.lines.forEach((l) => {
      const s = (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0);
      const rate = taxes.find((t) => t.code === l.tax_code)?.rate || 0;
      sub += s;
      tax += (s * rate) / 100;
    });
    return { sub, tax, total: sub + tax };
  }, [form.lines, taxes]);

  const submit = async () => {
    try {
      const payload = {
        ...form,
        currency: (form.currency || cur).toUpperCase(),
        exchange_rate_to_base: form.exchange_rate_to_base ? parseFloat(form.exchange_rate_to_base) : undefined,
        lines: form.lines.map((l) => ({ ...l, quantity: parseFloat(l.quantity) || 0, unit_price: parseFloat(l.unit_price) || 0 })),
      };
      await booksClient.post('/documents', payload);
      toast({ title: `${meta.singular} draft created` });
      onSaved();
    } catch (e) { toast({ title: errMsg(e), variant: 'destructive' }); }
  };

  // Sensible account defaults
  const suggested = kind === 'invoice'
    ? accounts.filter((a) => a.type === 'income')
    : accounts.filter((a) => a.type === 'expense' || a.type === 'asset');

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[95vh] overflow-auto p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-medium text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>New {meta.singular}</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-2">
          <F label={meta.contactType === 'customer' ? 'Customer' : 'Vendor'}>
            <select value={form.contact_id} onChange={(e) => setForm((f) => ({ ...f, contact_id: e.target.value }))} data-testid="doc-contact"
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm">
              <option value="">Select…</option>
              {contacts.map((c) => <option key={c.contact_id} value={c.contact_id}>{c.display_name || c.name}</option>)}
            </select>
          </F>
          <F label="Date"><input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="doc-date" className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></F>
          <F label="Reference"><input value={form.reference} onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></F>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <F label="Currency">
            <select value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} data-testid="doc-currency"
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm">
              {[cur, ...CCY_LIST.filter((c) => c !== cur)].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </F>
          {form.currency && form.currency !== cur && (
            <F label={`FX Rate — 1 ${form.currency} = ? ${cur}`}>
              <input type="number" step="0.00000001" value={form.exchange_rate_to_base}
                onChange={(e) => setForm((f) => ({ ...f, exchange_rate_to_base: e.target.value }))}
                data-testid="doc-fx-rate"
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm font-mono" />
              {fxLoading && <div className="text-[10px] mt-1 text-[#0b1a2e]/50">Looking up latest rate…</div>}
            </F>
          )}
        </div>

        <table className="w-full text-sm mb-3">
          <thead className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">
            <tr>
              <th className="text-left py-1">Description</th>
              <th className="text-left py-1 w-36">Account</th>
              <th className="text-right py-1 w-14">Qty</th>
              <th className="text-right py-1 w-24">Unit price</th>
              <th className="text-left py-1 w-28">Tax</th>
              <th className="text-left py-1 w-24">Cost Ctr</th>
              <th className="text-right py-1 w-24">Total</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {form.lines.map((l, i) => {
              const s = (parseFloat(l.quantity) || 0) * (parseFloat(l.unit_price) || 0);
              const rate = taxes.find((t) => t.code === l.tax_code)?.rate || 0;
              const total = s * (1 + rate / 100);
              return (
                <tr key={i} className="border-t border-slate-100">
                  <td className="py-1 pr-2"><input value={l.description} onChange={(e) => setLine(i, { description: e.target.value })} data-testid={`doc-line-${i}-desc`} className="w-full px-2 py-1 border border-slate-300 rounded text-xs" /></td>
                  <td className="py-1 pr-2">
                    <select value={l.account_id} onChange={(e) => setLine(i, { account_id: e.target.value })} data-testid={`doc-line-${i}-account`} className="w-full px-2 py-1 border border-slate-300 rounded text-xs">
                      <option value="">Select…</option>
                      {suggested.map((a) => <option key={a.account_id} value={a.account_id}>{a.code} — {a.name}</option>)}
                    </select>
                  </td>
                  <td className="py-1 pr-2"><input type="number" step="0.01" value={l.quantity} onChange={(e) => setLine(i, { quantity: e.target.value })} data-testid={`doc-line-${i}-qty`} className="w-full px-2 py-1 border border-slate-300 rounded text-xs text-right font-mono" /></td>
                  <td className="py-1 pr-2"><input type="number" step="0.01" value={l.unit_price} onChange={(e) => setLine(i, { unit_price: e.target.value })} data-testid={`doc-line-${i}-price`} className="w-full px-2 py-1 border border-slate-300 rounded text-xs text-right font-mono" /></td>
                  <td className="py-1 pr-2">
                    <select value={l.tax_code} onChange={(e) => setLine(i, { tax_code: e.target.value })} className="w-full px-2 py-1 border border-slate-300 rounded text-xs">
                      <option value="">— None —</option>
                      {taxes.filter((t) => kind === 'invoice' ? t.kind === 'output_tax' : t.kind === 'input_tax').map((t) => <option key={t.code} value={t.code}>{t.code}</option>)}
                    </select>
                  </td>
                  <td className="py-1 pr-2">
                    <select value={l.cost_center || ''} onChange={(e) => setLine(i, { cost_center: e.target.value })} data-testid={`doc-line-${i}-cc`} className="w-full px-2 py-1 border border-slate-300 rounded text-xs">
                      <option value="">—</option>
                      {(costCentres || []).map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
                    </select>
                  </td>
                  <td className="py-1 pr-2 text-right font-mono text-xs">{fmt(total, form.currency)}</td>
                  <td className="py-1">{form.lines.length > 1 && <button onClick={() => rmLine(i)} className="text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="text-xs">
            <tr><td colSpan="6" className="text-right pt-2 text-[#0b1a2e]/60">Subtotal</td><td className="text-right font-mono pt-2">{fmt(totals.sub, form.currency)}</td><td></td></tr>
            <tr><td colSpan="6" className="text-right text-[#0b1a2e]/60">Tax</td><td className="text-right font-mono">{fmt(totals.tax, form.currency)}</td><td></td></tr>
            <tr className="border-t"><td colSpan="6" className="text-right pt-1 text-[#0b1a2e] font-semibold">Grand total ({form.currency})</td><td className="text-right font-mono font-semibold text-[#0f5c3d]">{fmt(totals.total, form.currency)}</td><td></td></tr>
            {form.currency !== cur && form.exchange_rate_to_base && (
              <tr><td colSpan="6" className="text-right text-[#0b1a2e]/50 text-[10px]">≈ in {cur}</td><td className="text-right font-mono text-[10px]">{fmt(totals.total * parseFloat(form.exchange_rate_to_base || 0), cur)}</td><td></td></tr>
            )}
          </tfoot>
        </table>
        <button onClick={addLine} className="text-xs text-[#0f5c3d] font-semibold">+ Add line</button>

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#0b1a2e]/70">Cancel</button>
          <button onClick={submit} disabled={!form.contact_id || form.lines.some((l) => !l.account_id || !l.description)} data-testid="doc-save"
            className="px-5 py-2 bg-[#0f5c3d] text-white rounded-full text-sm font-semibold disabled:opacity-50">
            Save as Draft
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------- Payment / Receipt editor ----------------
const PaymentEditor = ({ doc, kind, accounts, cur, onClose, onSaved }) => {
  const docCur = doc.currency || cur;
  const [form, setForm] = useState({
    kind, date: new Date().toISOString().slice(0, 10),
    bank_account_id: '', reference: '', notes: '',
    amount: doc.amount_due,
    currency: docCur,
    exchange_rate_to_base: doc.exchange_rate_to_base || 1,
  });
  const [fxLoading, setFxLoading] = useState(false);
  const banks = accounts.filter((a) => a.subtype === 'bank' || a.subtype === 'cash');

  useEffect(() => {
    if (!form.currency || form.currency === cur) {
      setForm((f) => ({ ...f, exchange_rate_to_base: 1 })); return;
    }
    setFxLoading(true);
    booksClient.get('/fx/lookup', { params: { from_currency: form.currency, to_currency: cur, on_date: form.date } })
      .then((r) => setForm((f) => ({ ...f, exchange_rate_to_base: r.data.rate })))
      .finally(() => setFxLoading(false));
  }, [form.currency, form.date, cur]);

  const submit = async () => {
    try {
      await booksClient.post('/payments', {
        kind, date: form.date, contact_id: doc.contact_id,
        bank_account_id: form.bank_account_id, reference: form.reference, notes: form.notes,
        currency: form.currency, exchange_rate_to_base: parseFloat(form.exchange_rate_to_base) || 1,
        allocations: [{ doc_id: doc.doc_id, amount: parseFloat(form.amount) }],
      });
      toast({ title: kind === 'receipt' ? 'Receipt recorded' : 'Payment recorded' });
      onSaved();
    } catch (e) { toast({ title: errMsg(e), variant: 'destructive' }); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-medium text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {kind === 'receipt' ? 'Record Receipt' : 'Record Payment'}
          </h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="text-xs text-[#0b1a2e]/60 mb-4 bg-[#f7fbf8] rounded p-3 border border-[#0f5c3d]/10">
          Against {doc.doc_no} · due {fmt(doc.amount_due, docCur)}
        </div>
        <div className="space-y-3">
          <F label="Date"><input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></F>
          <F label="Bank / Cash account">
            <select value={form.bank_account_id} onChange={(e) => setForm((f) => ({ ...f, bank_account_id: e.target.value }))} data-testid="pay-bank"
              className="w-full px-3 py-2 border border-slate-300 rounded text-sm">
              <option value="">Select…</option>
              {banks.map((b) => <option key={b.account_id} value={b.account_id}>{b.code} — {b.name}</option>)}
            </select>
          </F>
          <F label={`Amount (in ${docCur})`}>
            <input type="number" step="0.01" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} data-testid="pay-amount" className="w-full px-3 py-2 border border-slate-300 rounded text-sm font-mono" />
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Payment currency">
              <select value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} data-testid="pay-currency"
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm">
                {[cur, ...CCY_LIST.filter((c) => c !== cur)].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </F>
            {form.currency !== cur && (
              <F label={`FX 1 ${form.currency} = ${cur}`}>
                <input type="number" step="0.00000001" value={form.exchange_rate_to_base}
                  onChange={(e) => setForm((f) => ({ ...f, exchange_rate_to_base: e.target.value }))}
                  data-testid="pay-fx-rate"
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm font-mono" />
                {fxLoading && <div className="text-[10px] text-[#0b1a2e]/50">Looking up…</div>}
              </F>
            )}
          </div>
          <F label="Reference"><input value={form.reference} onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></F>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-[#0b1a2e]/70">Cancel</button>
          <button onClick={submit} disabled={!form.bank_account_id || !form.amount} data-testid="pay-save"
            className="px-5 py-2 bg-[#0f5c3d] text-white rounded-full text-sm font-semibold disabled:opacity-50">
            Record
          </button>
        </div>
      </div>
    </div>
  );
};

const F = ({ label, children }) => (
  <label className="block">
    <div className="text-[10px] font-semibold uppercase tracking-widest text-[#0b1a2e]/60 mb-1">{label}</div>
    {children}
  </label>
);

export default DocumentsPage;
